import { getAuth } from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-auth.js';
import { getApp } from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-app.js';
import { getFunctions, httpsCallable } from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-functions.js';

let allDirectoryUsers = [];
let presenceUsers = [];
let mergedUsers = [];
let currentFilter = 'all';
let currentSearch = '';
let sortColumn = 'lastActive';
let sortDirection = 'desc';
let page = 1;
let pageSize = 30;
let totalPages = 1;

function openModal() {
  const overlay = document.getElementById('community-modal');
  if (overlay) overlay.classList.remove('hidden');
}

function closeModal() {
  const overlay = document.getElementById('community-modal');
  if (overlay) overlay.classList.add('hidden');
}

function bindOpeners() {
  const btn1 = document.getElementById('presence-count-btn-right');
  const btn2 = document.getElementById('pw-count-btn');
  const openMini = document.getElementById('open-community-modal-btn-right');
  [btn1, btn2].forEach(btn => {
    if (btn) {
      btn.addEventListener('click', () => {
        openModal();
        initializeData();
      });
    }
  });
  if (openMini) {
    openMini.addEventListener('click', () => {
      openModal();
      initializeData();
    });
  }
}

async function fetchDirectoryUsers() {
  const app = getApp();
  const functions = getFunctions(app, 'us-east1');
  const getCommunityUsers = httpsCallable(functions, 'getCommunityUsers');
  const result = await getCommunityUsers({ page: 1, limit: 150, search: '', statusFilter: 'all', sortColumn: 'lastActive', sortDirection: 'desc' });
  if (!result.data?.success) throw new Error('Failed to load users');
  allDirectoryUsers = result.data.users || [];
}

async function fetchPresenceUsers() {
  const { listenForLoungePresence } = await import('../modules/firestore/firestoreService.js');
  await new Promise((resolve) => {
    const unsub = listenForLoungePresence(async (users) => {
      presenceUsers = users || [];
      if (unsub) unsub();
      resolve();
    });
  });
}

function buildFeaturedRow(users) {
  const row = document.getElementById('community-featured-row');
  if (!row) return;
  row.innerHTML = '';
  users.forEach((u) => {
    const el = document.createElement('button');
    el.className = 'lounge-user-pin';
    el.style.setProperty('background-image', `url('${u.profilePicUrl || 'https://placehold.co/24x24/1f2937/4b5563?text=?'}')`, 'important');
    el.style.setProperty('background-position', 'center center', 'important');
    el.style.setProperty('background-size', 'cover', 'important');
    el.innerHTML = `<div class="lounge-pin-overlay"></div><span class="lounge-pin-name-row"><span class="lounge-pin-status-dot ${u.connectedShowId ? 'status-live' : 'status-around'}"></span><span class="lounge-pin-username">${u.username || u.displayName || 'User'}</span></span>`;
    el.addEventListener('click', async () => {
      const { showArtistProfileModal } = await import('../modules/artistProfileModal.js');
      await showArtistProfileModal(u.id, u.username || u.displayName, u.profilePicUrl);
    });
    row.appendChild(el);
  });
}

function renderMiniFeaturedRow() {
  const mini = document.getElementById('community-featured-mini-row');
  if (!mini) return;
  mini.innerHTML = '';
  const max = 10;
  const list = presenceUsers.slice(0, max);
  list.forEach((u) => {
    const el = document.createElement('div');
    el.className = 'lounge-user-pin';
    el.style.width = '80px';
    el.style.minWidth = '80px';
    el.style.setProperty('background-image', `url('${u.profilePicUrl || 'https://placehold.co/24x24/1f2937/4b5563?text=?'}')`, 'important');
    el.style.setProperty('background-position', 'center center', 'important');
    el.style.setProperty('background-size', 'cover', 'important');
    el.innerHTML = `<div class="lounge-pin-overlay"></div><span class="lounge-pin-name-row"><span class="lounge-pin-status-dot ${u.connectedShowId ? 'status-live' : 'status-around'}"></span><span class="lounge-pin-username" style="font-size:10px;">${(u.username || u.displayName || 'User').slice(0,10)}</span></span>`;
    mini.appendChild(el);
  });
}

function applyFilterAndSort() {
  const byId = new Map();
  presenceUsers.forEach((u) => byId.set(u.id, { ...u }));
  allDirectoryUsers.forEach((u) => {
    if (byId.has(u.id)) {
      byId.set(u.id, { ...byId.get(u.id), ...u });
    } else {
      byId.set(u.id, u);
    }
  });
  let list = Array.from(byId.values());
  if (currentFilter === 'live') list = list.filter(u => !!u.connectedShowId);
  else if (currentFilter === 'around') list = list.filter(u => !u.connectedShowId && (u.lastActive || u.lastSeen));
  else if (currentFilter === 'loggedin') list = list.filter(u => !!(u.lastActive || u.lastSeen));
  if (currentSearch) {
    const q = currentSearch.toLowerCase();
    list = list.filter(u => (u.displayName || u.username || '').toLowerCase().includes(q));
  }
  const dir = sortDirection === 'asc' ? 1 : -1;
  list.sort((a, b) => {
    if (sortColumn === 'displayName') return ((a.displayName || a.username || '').localeCompare(b.displayName || b.username || '')) * dir;
    if (sortColumn === 'lastActive') return ((b.lastActive || b.lastSeen || 0) - (a.lastActive || a.lastSeen || 0)) * (dir === 1 ? -1 : 1);
    return ((b[sortColumn] || 0) - (a[sortColumn] || 0)) * (dir === 1 ? -1 : 1);
  });
  mergedUsers = list;
  totalPages = Math.max(1, Math.ceil(mergedUsers.length / pageSize));
  if (page > totalPages) page = totalPages;
}

function renderList(preserveScroll = false) {
  const listEl = document.getElementById('community-result-list');
  const emptyEl = document.getElementById('community-empty');
  const pagerStatus = document.getElementById('community-page-status');
  const prevBtn = document.getElementById('community-page-prev');
  const nextBtn = document.getElementById('community-page-next');
  if (!listEl) return;

  const start = (page - 1) * pageSize;
  const pageItems = mergedUsers.slice(start, start + pageSize);
  if (pageItems.length === 0) {
    emptyEl.style.display = 'block';
  } else {
    emptyEl.style.display = 'none';
  }

  if (!preserveScroll) {
    listEl.scrollTop = 0;
  }

  const rowHeight = 58; // 50px pin + 8px gap
  const viewportHeight = listEl.clientHeight || 120;
  const scrollTop = listEl.scrollTop || 0;
  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - 2);
  const visibleCount = Math.ceil(viewportHeight / rowHeight);
  const endIndex = Math.min(pageItems.length, startIndex + visibleCount + 4);

  listEl.innerHTML = '';
  listEl.style.paddingTop = `${startIndex * rowHeight}px`;
  listEl.style.paddingBottom = `${(pageItems.length - endIndex) * rowHeight}px`;

  const visibleItems = pageItems.slice(startIndex, endIndex);
  visibleItems.forEach((u) => {
    const pin = document.createElement('div');
    pin.className = 'lounge-user-pin';
    const statusClass = u.connectedShowId ? 'status-live' : (u.lastActive || u.lastSeen ? 'status-around' : 'status-offline');
    pin.title = u.connectedShowId ? 'Status: Live' : (u.lastActive || u.lastSeen ? 'Status: Around' : 'Status: Offline');
    pin.style.setProperty('background-image', `url('${u.profilePicUrl || 'https://placehold.co/24x24/1f2937/4b5563?text=?'}')`, 'important');
    pin.style.setProperty('background-position', 'center center', 'important');
    pin.style.setProperty('background-size', 'cover', 'important');
    pin.innerHTML = `
      <div class="lounge-pin-overlay"></div>
      <span class="lounge-pin-name-row">
        <span class="lounge-pin-status-dot ${statusClass}"></span>
        <span class="lounge-pin-username">${u.displayName || u.username || 'User'}</span>
      </span>`;
    pin.addEventListener('click', async () => {
      const { showArtistProfileModal } = await import('../modules/artistProfileModal.js');
      await showArtistProfileModal(u.id, u.displayName || u.username, u.profilePicUrl);
    });
    listEl.appendChild(pin);
  });

  pagerStatus.textContent = `Page ${page} of ${totalPages}`;
  prevBtn.disabled = page <= 1;
  nextBtn.disabled = page >= totalPages;
}

function bindControls() {
  const closeBtn = document.getElementById('community-modal-close-btn');
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  const overlay = document.getElementById('community-modal');
  if (overlay) overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
  const chips = document.querySelectorAll('.community-chip');
  chips.forEach(ch => ch.addEventListener('click', () => {
    chips.forEach(x => x.classList.remove('active'));
    ch.classList.add('active');
    currentFilter = ch.dataset.filter;
    page = 1;
    applyFilterAndSort();
    renderFeatured();
    renderList();
  }));
  const search = document.getElementById('community-search-input');
  const clear = document.getElementById('community-search-clear');
  if (search) {
    let t;
    search.addEventListener('input', () => {
      clearTimeout(t);
      t = setTimeout(() => {
        currentSearch = search.value.trim();
        page = 1;
        applyFilterAndSort();
        renderFeatured();
        renderList();
      }, 200);
    });
  }
  if (clear) clear.addEventListener('click', () => {
    const searchEl = document.getElementById('community-search-input');
    if (searchEl) searchEl.value = '';
    currentSearch = '';
    page = 1;
    applyFilterAndSort();
    renderFeatured();
    renderList();
  });
  const sortSel = document.getElementById('community-sort-select');
  const sortDirBtn = document.getElementById('community-sort-dir-btn');
  if (sortSel) sortSel.addEventListener('change', () => {
    sortColumn = sortSel.value;
    applyFilterAndSort();
    renderList();
  });
  if (sortDirBtn) sortDirBtn.addEventListener('click', () => {
    sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    sortDirBtn.innerHTML = `<i class="fas ${sortDirection === 'asc' ? 'fa-sort-amount-up' : 'fa-sort-amount-down'}"></i>`;
    applyFilterAndSort();
    renderList();
  });
  const prevBtn = document.getElementById('community-page-prev');
  const nextBtn = document.getElementById('community-page-next');
  if (prevBtn) prevBtn.addEventListener('click', () => { if (page > 1) { page--; renderList(); } });
  if (nextBtn) nextBtn.addEventListener('click', () => { if (page < totalPages) { page++; renderList(); } });
}

function renderFeatured() {
  let featured = presenceUsers;
  if (currentFilter === 'live') featured = presenceUsers.filter(u => !!u.connectedShowId);
  else if (currentFilter === 'around') featured = presenceUsers.filter(u => !u.connectedShowId);
  else if (currentFilter === 'loggedin') featured = presenceUsers.filter(u => !!(u.lastActive || u.lastSeen));
  if (currentSearch) {
    const q = currentSearch.toLowerCase();
    featured = featured.filter(u => (u.displayName || u.username || '').toLowerCase().includes(q));
  }
  buildFeaturedRow(featured);
}

async function initializeData() {
  try {
    await Promise.all([fetchDirectoryUsers(), fetchPresenceUsers()]);
    applyFilterAndSort();
    renderFeatured();
    renderList();
    renderMiniFeaturedRow();
  } catch (e) {
    console.error('[CommunityModal] Error initializing:', e);
  }
}

function init() {
  bindOpeners();
  bindControls();

  const listEl = document.getElementById('community-result-list');
  if (listEl) {
    listEl.addEventListener('scroll', () => renderList(true));
  }

  document.addEventListener('presenceUpdate', (e) => {
    const { liveUsers = [], aroundUsers = [], mode } = e.detail || {};

    presenceUsers = [...liveUsers, ...aroundUsers];
    renderMiniFeaturedRow();

    const overlay = document.getElementById('community-modal');
    const isOpen = overlay && !overlay.classList.contains('hidden');
    if (isOpen) {
      applyFilterAndSort();
      renderFeatured();
      renderList(true);
    }
  });
}

document.addEventListener('DOMContentLoaded', init);
